import { ApolloClient, InMemoryCache, ApolloLink, NetworkStatus } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { makeLogout } from 'src/utils/auth';
import { getAccessToken } from 'src/utils/tokenService';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { captureException } from '@sentry/react';
import { showErrorToast } from 'src/components/Toast';
import { gqlLog } from 'src/utils/logger';
import { maybeRedirectToWorkspaceSwitch } from 'src/utils/workspaceSwitch';

import { API_DEBUG, isDev } from 'src/config';

const uploadLink = createUploadLink({ uri: `${process.env.BASE_URL}api/public/api/graphql` });

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    // DIAGNOSTIC: dump every GraphQL error's extensions so we can see whether the API is
    // emitting the workspace-switch signal on cross-workspace reads. Silenced in production.
    gqlLog.debug(
      `errorLink: ${graphQLErrors.length} GraphQL error(s) on "${operation.operationName}"`,
      graphQLErrors.map(({ message, extensions }) => ({ message, extensions })),
    );

    // Cross-workspace read: the API signals the target workspace via
    // `extensions.workspaceSwitch` on the four single-object read queries. Hand off to the
    // same `/error` interstitial the REST 409 path uses (confirm + setActiveWorkspace) and
    // stop — this is an expected redirect, not a toast/Sentry-worthy failure.
    const switchTarget = maybeRedirectToWorkspaceSwitch(graphQLErrors);
    if (switchTarget) {
      gqlLog.debug('errorLink: workspace-switch signal detected → redirecting to /error', switchTarget);
      return;
    }

    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const category = extensions?.category ? String(extensions.category) : undefined;
      const validation = (extensions as { validation?: Record<string, unknown> } | undefined)?.validation;
      // The AI BE surfaces genuine engine/RIE failures as a ValidationException keyed `rie` — those
      // are real incidents, so capture them. Ordinary field validations / authz / authn are expected,
      // user-facing outcomes (shown as toasts), not bugs — skip those.
      const isEngineFailure = !!validation && typeof validation === 'object' && 'rie' in validation;
      const isExpected =
        !isEngineFailure &&
        ((category && ['validation', 'authorization', 'authentication'].includes(category)) || Boolean(validation));
      const isHandledAuth = message === 'Unauthenticated.';

      if (!isExpected && !isHandledAuth) {
        // Static title + explicit fingerprint keeps grouping stable and keeps the
        // dynamic server `message` (which can contain work data) out of the most
        // visible field. Only structural, non-free-text context is attached.
        const title = isEngineFailure ? 'AI engine failure' : 'GraphQL operation error';
        const ctx = isEngineFailure ? 'engine_failure' : 'graphql_error';
        captureException(new Error(title), {
          tags: {
            context: ctx,
            operation: operation.operationName ?? 'unknown',
            category: category ?? 'unknown',
          },
          fingerprint: [ctx, operation.operationName ?? 'unknown', category ?? 'unknown'],
          extra: {
            path: JSON.stringify(path),
            locations: JSON.stringify(locations),
            extensionsCode: (extensions as { code?: unknown } | undefined)?.code,
          },
        });
      }

      if (isHandledAuth) {
        makeLogout({ noEvents: true });
      }
      showErrorToast(message);
    });
  }
  if (networkError) {
    showErrorToast('Network error. Please check your connection.');
    // Do NOT serialize the whole networkError — ServerError/ServerParseError embed the
    // raw response body (.result/.bodyText), which can echo submitted fields and, in
    // debug mode, SQL/stack traces. Attach an allowlist of structural fields only.
    captureException(networkError, {
      tags: {
        context: 'network_error',
        operation: operation.operationName ?? 'unknown',
      },
      extra: {
        name: networkError.name,
        statusCode: (networkError as { statusCode?: number }).statusCode,
      },
    });
  }
});

const authLink = setContext(async (operation, { headers }) => {
  const token = await getAccessToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : undefined,
    },
  };
});

// --- DEBUG: Log all GQL operations ---
let gqlRequestCount = 0;
const loggingLink = new ApolloLink((operation, forward) => {
  gqlRequestCount++;
  const num = gqlRequestCount;
  const firstDefinition = operation.query.definitions[0];
  const operationType = firstDefinition?.kind === 'OperationDefinition' ? firstDefinition.operation : 'unknown';
  gqlLog.debug(`#${num} ${operationType.toUpperCase()} ${operation.operationName}`, operation.variables);
  const startTime = performance.now();
  return forward(operation).map((result) => {
    const duration = Math.round(performance.now() - startTime);
    // DIAGNOSTIC: surface any GraphQL errors carried in the (HTTP-200) response body so we
    // can see, per operation, whether the API returned data or an error such as the
    // workspace-switch signal. Only messages + extensions are logged, never `data`.
    const errors = result.errors;
    if (errors?.length) {
      gqlLog.debug(
        `#${num} ${operation.operationName} completed in ${duration}ms with ${errors.length} error(s)`,
        errors.map(({ message, extensions }) => ({ message, extensions })),
      );
    } else {
      gqlLog.debug(`#${num} ${operation.operationName} completed in ${duration}ms (no errors)`);
    }
    return result;
  });
});
// --- END DEBUG ---

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([...(isDev && API_DEBUG ? [loggingLink] : []), authLink, errorLink, uploadLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          scenario(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Scenario', id: args?.id });
          },
          control(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Control', id: args?.id });
          },
          incident(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Incident', id: args?.id });
          },
        },
      },
      OrgUnitIdentity: { merge: true },
      OrgUnitActions: { merge: true },
      WorkspaceUserActions: { merge: true },
      OrganisationUserActions: { merge: true },
      ScenarioRisk: { merge: true },
      AssessmentRisk: { merge: true },
      Meta: { merge: true },
      Owner: { merge: true },
      // AI Risk Assistant (ticket 7127). Keyed on uxId (not id); used by the future GraphQL
      // service so session updates normalise correctly. The mock path does not use Apollo.
      AiAssistantSession: { keyFields: ['uxId'] },
    },
  }),

  defaultOptions: {
    watchQuery: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
    query: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-first',
    },
  },
});

type IsQueryLoading = (options: { loading: boolean; data: unknown; networkStatus: NetworkStatus }) => boolean;

export const isQueryLoading: IsQueryLoading = ({ loading, data, networkStatus }) => {
  return (loading && !data) || networkStatus === NetworkStatus.refetch;
};
